// Data layer for the new (dev-only) Show Page — see .claude/show-slots-prototype.html
// for the spec this is built against, and src/pages/Shows/ShowPage.jsx for the
// component that consumes it.
//
// Deliberately reuses the SAME hooks the classic builder (CampaignSetup.jsx) uses
// (useGetCampaign, useGetCampaignLayerContent from ./campaigns, useGetPlaylistLayerContent
// from ./playlists) rather than new endpoints — this page reads the same draft-side
// tables the classic builder edits, it just presents them differently.
//
// "Mixed" (can this layer safely collapse to one swappable slot?) is NOT computed
// here — it's read from GET /campaign/{id}/layer-mixedness (App\Support\
// CampaignLayerMixedness on the backend). An earlier cut of this file computed it
// client-side and got it wrong: it treated any layer with more than one content
// row as mixed, which flagged a third of a real company's shows on dev, every one
// of them just several photos with identical settings. Divergence, not row count,
// is what actually can't collapse — and that's exactly the kind of "reuse the
// server's answer, don't recompute" mistake this app already learned once with
// has_unpublished_changes. Not repeating it here.
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useGetCampaign, useGetCampaignLayerContent, useGetCampaignLayerMixedness } from "./campaigns";
import { useGetPlaylistLayerContent, useCompanyPlaylists } from "./playlists";
import axiosInstance from "../config/Axios";

export { useCompanyPlaylists as useCompanyPlaylistsForPicker };

// The four layer keys are a hardcoded DB enum (campaign_playlist_media.layer /
// playlist_media.type) — not configurable, confirmed against the migration.
export const LAYERS = ["MAIN", "PLAYGROUND", "CORNER", "BANNER"];

// role labels ported verbatim from the prototype (COMPS.stack.slots / VENUE).
export const ROLE_KEY_BY_LAYER = {
    MAIN: "role_main",
    PLAYGROUND: "role_playground",
    CORNER: "role_corner",
    BANNER: "role_banner",
};

// Which layers are "this show's content" (COMPS.stack.slots) vs. "fixed in
// place" (VENUE) — a UX grouping over the same 4 backend layers, not a
// separate backend concept. See the brief: "VENUE-PERSISTENT... is the same
// idea... on different axes."
export const SHOW_LAYERS = ["MAIN", "PLAYGROUND"];
export const VENUE_LAYERS = ["CORNER", "BANNER"];

// Composition picker, display-only (COMPS in the prototype) — "stack" is the
// only one any real show is in today (see Stage/Slot in ShowPage.jsx), so the
// other three render inert: visible, matching the prototype's diagrams, but
// not selectable. Wiring them up is phase 4 (tiled compositions).
export const COMPOSITIONS = [
    { id: "stack", kind: "stack", labelKey: "composition_stack_label" },
    { id: "halves", kind: "tile", cols: "1fr 1fr", rows: "1fr", cells: 2, labelKey: "composition_halves_label" },
    { id: "mainside", kind: "tile", cols: "2fr 1fr", rows: "1fr", cells: 2, labelKey: "composition_mainside_label" },
    { id: "quarters", kind: "tile", cols: "1fr 1fr", rows: "1fr 1fr", cells: 4, labelKey: "composition_quarters_label" },
];

// Add-affordance copy for VENUE_LAYERS that aren't filled yet — ported
// verbatim from the prototype's VENUE[].desc.
export const VENUE_DESC_KEY_BY_LAYER = {
    CORNER: "venue_add_corner_desc",
    BANNER: "venue_add_banner_desc",
};

// "الأنسب هنا" — what suits each layer, ported from the prototype's own
// per-slot line. The prototype only rendered it on an EMPTY slot; every real
// show's slots are full, so it never actually appeared — a design bug, not a
// missing feature. Fixed by showing it unconditionally (fill or no fill) and
// in both places a choice gets made: the slot card itself and the picker
// that opens from it. A plain layer→i18n-key map (not literal text in the
// component) so a business-type profile can swap these later per company
// without touching ShowPage.jsx or Picker/VenuePicker — same shape as
// ROLE_KEY_BY_LAYER/VENUE_DESC_KEY_BY_LAYER above, for the same reason.
export const SLOT_GUIDANCE_KEY_BY_LAYER = {
    MAIN: "guidance_main",
    PLAYGROUND: "guidance_playground",
    CORNER: "guidance_corner",
    BANNER: "guidance_banner",
};

/**
 * Picks what a layer's pivot rows resolve to, for DISPLAY only — not for
 * deciding whether it's mixed (that's the backend's job, see above).
 *   - 0 rows: empty.
 *   - 1 row: a single playlist / media / layout reference — named directly.
 *   - >1 rows: several items in one layer. Still readable — "the slot itself
 *     must still be readable — it shows what is in it" — just not a single
 *     named entity, so it gets a count instead of a name. This is true
 *     whether or not the backend says those rows disagree (mixed); a mixed
 *     multi-row layer isn't "empty", it has real content, it just can't be
 *     swapped from here.
 */
export function deriveFill(rows) {
    if (!rows || rows.length === 0) return null;

    if (rows.length > 1) {
        // Still real content with no single name — but the first row's own
        // media.* columns (joined directly onto campaign_playlist_media, see
        // CampaignPlaylistController::getCampaignContent()) are enough for a
        // one-frame preview thumbnail when the set is direct media rows
        // (the common MAIN case: several audio/image files, no playlist
        // wrapper). If rows[0] is itself a playlist reference, it carries no
        // media.thumb — previewThumb below falls back to that layer's fetched
        // playlist items instead.
        return { kind: "multi", count: rows.length, thumb: rows[0]?.thumb || null, mediaType: rows[0]?.type || null };
    }

    const row = rows[0];
    if (row.playlist_id) {
        return { kind: "playlist", id: row.playlist_id, name: row.playlist_title };
    }
    if (row.layout_id) {
        // Tiled-composition territory (phase 4) — MAIN referencing a Layout.
        // Phase 1 is stack-only, so this is surfaced but not specially handled yet.
        return { kind: "layout", id: row.layout_id, name: row.layout_title };
    }
    if (row.media_id) {
        return { kind: "media", id: row.media_id, name: row.title || row.file_name, thumb: row.thumb || null, mediaType: row.type || null };
    }
    return null;
}

function sumDuration(items) {
    if (!items || items.length === 0) return 0;
    return items.reduce((sum, it) => sum + (Number(it.custom_duration) || Number(it.default_duration) || 0), 0);
}

/**
 * Composes one show's read model for the four fixed layers. All react-query
 * hooks below are called unconditionally, every render — the layer set is a
 * fixed constant (LAYERS), not a runtime-length list, so this doesn't violate
 * the rules of hooks even though some queries are gated by data the other
 * queries produce (dependent-query pattern).
 */
export function useShowPageData(campaignId) {
    const enabled = !!campaignId;

    const campaignQ = useGetCampaign({ id: campaignId, enabled });
    const mixednessQ = useGetCampaignLayerMixedness(campaignId, enabled);

    const mainContentQ = useGetCampaignLayerContent(campaignId, "MAIN");
    const playgroundContentQ = useGetCampaignLayerContent(campaignId, "PLAYGROUND");
    const cornerContentQ = useGetCampaignLayerContent(campaignId, "CORNER");
    const bannerContentQ = useGetCampaignLayerContent(campaignId, "BANNER");

    const contentByLayer = {
        MAIN: mainContentQ,
        PLAYGROUND: playgroundContentQ,
        CORNER: cornerContentQ,
        BANNER: bannerContentQ,
    };

    const fillByLayer = {};
    LAYERS.forEach((layer) => {
        fillByLayer[layer] = deriveFill(contentByLayer[layer].data);
    });

    // Dependent queries, display metadata only (item count / duration) — fixed
    // 4 calls (one per layer), fired only once the layer resolves to a single
    // playlist reference.
    const mainItemsQ = useGetPlaylistLayerContent(
        fillByLayer.MAIN?.kind === "playlist" ? fillByLayer.MAIN.id : null,
        "MAIN"
    );
    const playgroundItemsQ = useGetPlaylistLayerContent(
        fillByLayer.PLAYGROUND?.kind === "playlist" ? fillByLayer.PLAYGROUND.id : null,
        "PLAYGROUND"
    );
    const cornerItemsQ = useGetPlaylistLayerContent(
        fillByLayer.CORNER?.kind === "playlist" ? fillByLayer.CORNER.id : null,
        "CORNER"
    );
    const bannerItemsQ = useGetPlaylistLayerContent(
        fillByLayer.BANNER?.kind === "playlist" ? fillByLayer.BANNER.id : null,
        "BANNER"
    );

    const itemsByLayer = {
        MAIN: mainItemsQ,
        PLAYGROUND: playgroundItemsQ,
        CORNER: cornerItemsQ,
        BANNER: bannerItemsQ,
    };

    const slots = {};
    LAYERS.forEach((layer) => {
        const items = itemsByLayer[layer].data;
        const rows = contentByLayer[layer].data;
        const fill = fillByLayer[layer];

        // One-frame preview data — a still image standing in for "how this
        // looks on screen", not playback. "media"/"multi" carry their own
        // thumb straight off the pivot row (media.* is joined directly, see
        // deriveFill above); "playlist" has none of its own, so it borrows
        // its first resolved item's thumb from the dependent items query
        // that's already being fetched for the item-count/duration display.
        const previewThumb =
            fill?.kind === "media" || fill?.kind === "multi" ? fill.thumb : fill?.kind === "playlist" ? items?.[0]?.thumb : null;
        const previewType =
            fill?.kind === "media" || fill?.kind === "multi" ? fill.mediaType : fill?.kind === "playlist" ? items?.[0]?.type : null;

        slots[layer] = {
            layer,
            fill,
            mixed: mixednessQ.data?.[layer] === true,
            itemCount: items?.length,
            durationSeconds: fillByLayer[layer]?.kind === "playlist" ? sumDuration(items) : 0,
            previewThumb: previewThumb || null,
            previewType: previewType || null,
            // The pivot rows' own ids — what a swap deletes before pointing the
            // layer at something new. getCampaignContent() aliases this
            // explicitly as campaign_playlist_media_id (campaign_playlist_media.*
            // and playlist.* are both selected raw after it, and playlist.id
            // would otherwise silently win the plain `id` key on the hydrated
            // row) — falling back to `id` only if a future response shape drops
            // the alias.
            rowIds: (rows || []).map((r) => r.campaign_playlist_media_id ?? r.id).filter(Boolean),
            // Raw rows, kept around (not just the derived `fill` summary) so
            // composition switching can migrate this layer's actual content
            // elsewhere (see deriveMigratableRef) — deriveFill's own output
            // drops the underlying playlist_id/media_id list for a "multi"
            // fill down to just the first row's, which isn't enough to
            // rebuild what a multi-item layer actually held.
            rawRows: rows || [],
            // is_muted is a real, already-selected column on campaign_playlist_media
            // (campaign_playlist_media.* is selected raw by getCampaignContent()) —
            // audible = "not muted". Used to derive which layer is the current audio
            // source without a separate field for it (see useSetLayerAudioSource).
            rowCount: (rows || []).length,
            audibleRowCount: (rows || []).filter((r) => !Number(r.is_muted)).length,
            // getCampaignContent() aliases campaign_playlist_media.settings as
            // media_settings and json_decodes it server-side — the same shape
            // useSetPlaygroundGeometry writes (width/height/top_margin/start_margin).
            geometry: rows?.[0]?.media_settings || null,
        };
    });

    const isLoading =
        campaignQ.isLoading ||
        mixednessQ.isLoading ||
        LAYERS.some((l) => contentByLayer[l].isLoading);

    return {
        isLoading,
        isError: campaignQ.isError,
        campaign: campaignQ.data,
        slots,
    };
}

// ─── Phase 2: the picker's data, and the write path ────────────────────────
// New, minimal hooks rather than reusing useAddCampaignContent/
// useDeleteCampaignContent from campaignContent.js — those dispatch Redux
// actions (setDisableBtn, toggleDialog("addContent"), setMediaIdReset) that
// belong to the classic builder's own dialog/orchestration and don't exist
// on this page; importing them here would fire actions at UI that isn't
// mounted. Same raw endpoints, page-scoped side effects instead.

// "من مكتبة المحتوى" tab — root-level media only (no folder navigation).
// MediaList.jsx's folder-aware version is tightly coupled to Redux
// (params.folder_id, BreadCrumb) for a feature this phase doesn't need;
// scoped down deliberately, not an oversight.
const getMediaLibraryPage = async ({ queryKey }) => {
    const [, layer, search] = queryKey;
    const { data } = await axiosInstance.get("/media", {
        params: { section: "MEDIA", layer, search: search || undefined, per_page: 60 },
    });
    return data.data;
};

export const useMediaLibraryForPicker = (layer, search, enabled = true) => {
    return useQuery(["showPageMediaLibrary", layer, search], getMediaLibraryPage, {
        enabled,
        keepPreviousData: true,
        staleTime: 1000 * 60,
    });
};

// GET /playlist/{id}/publish-impact — called at SELECTION time, before the
// write, so the "used elsewhere" disclosure is visible before the user
// commits to a swap, not after.
const getPlaylistPublishImpact = async ({ queryKey }) => {
    const [, playlistId] = queryKey;
    const { data } = await axiosInstance.get(`/playlist/${playlistId}/publish-impact`);
    return data.data;
};

export const usePlaylistPublishImpact = (playlistId) => {
    return useQuery(["playlistPublishImpact", playlistId], getPlaylistPublishImpact, {
        enabled: !!playlistId,
        staleTime: 1000 * 30,
    });
};

function invalidateShowPageLayer(queryClient, campaignId, layer) {
    queryClient.invalidateQueries(["getCampaignLayerContent", campaignId, layer]);
    queryClient.invalidateQueries(["getCampaignLayerMixedness", campaignId]);
    queryClient.invalidateQueries(["getCampaign", campaignId]);
}

// A write INTO A REGION targets that region's own screen campaign_id, not
// the top-level show's campaign_id — invalidateShowPageLayer's keys are all
// keyed by the TOP-level campaign, so they're harmless no-ops for a region
// write (nothing caches under a screen's own campaign_id). What actually
// needs to refetch is useCurrentCompositionRegions' own cache entry, keyed
// by layoutId — shared by both the Stage preview and the content-section's
// region slots, so one invalidation refreshes both.
function invalidateCompositionRegions(queryClient, layoutId) {
    if (layoutId) queryClient.invalidateQueries(["showPageCompositionRegions", layoutId]);
}

/**
 * "من قوائمك" tab, existing playlist: point the layer at it.
 *
 * assignCampaignContent() only ever ADDS a campaign_playlist_media row — it
 * has never been a swap. A real swap is add-then-delete, in that order
 * deliberately: if the delete step fails, the layer is left with both the
 * old and new content referenced (reads as "multi", still real content) —
 * not empty. An empty MAIN is the one failure mode CampaignPlayability
 * exists to catch at assignment time; this ordering just avoids manufacturing
 * that case as a side effect of a half-finished swap.
 */
export const useSwapLayerToPlaylist = () => {
    const queryClient = useQueryClient();
    return useMutation(
        async ({ campaignId, layer, playlistId, oldRowIds }) => {
            // "بدون محتوى" calls this with playlistId: null — CampaignContentRequest
            // requires every ids_list entry when type=playlist, so `ids_list:[null]`
            // would 422. The comment above confirmPlaylist's own caller already
            // documented "the assign POST is simply skipped" as the intended
            // behavior; it just wasn't actually implemented here.
            if (playlistId) {
                await axiosInstance.post("/campaign/content/assign", {
                    campaign_id: campaignId,
                    layer,
                    type: "playlist",
                    ids_list: [playlistId],
                });
            }
            for (const rowId of oldRowIds || []) {
                await axiosInstance.delete(`/campaign/content/${rowId}/delete`);
            }
        },
        {
            onSuccess: (_, { campaignId, layer, layoutId }) => {
                invalidateShowPageLayer(queryClient, campaignId, layer);
                invalidateCompositionRegions(queryClient, layoutId);
            },
        }
    );
};

/** "من مكتبة المحتوى" tab: POST /campaign/{id}/layer/{layer}/media-playlist. */
export const useCreateLayerMediaPlaylist = () => {
    const queryClient = useQueryClient();
    return useMutation(
        async ({ campaignId, layer, mediaIds, roleLabel }) => {
            const { data } = await axiosInstance.post(
                `/campaign/${campaignId}/layer/${layer}/media-playlist`,
                { media_ids: mediaIds, role_label: roleLabel }
            );
            return data.data;
        },
        {
            onSuccess: (_, { campaignId, layer, layoutId }) => {
                invalidateShowPageLayer(queryClient, campaignId, layer);
                invalidateCompositionRegions(queryClient, layoutId);
            },
        }
    );
};

// ─── التركيب — tiled compositions map onto the existing Layout feature ─────
// A Layout is a geometry container (`screens_setting`, percent rects) plus
// one real, independent Campaign per screen (LayoutController::
// ensureCampaignsForScreens()) — each screen gets its own content the exact
// same way any campaign does (its own MAIN/PLAYGROUND/etc via /campaign/
// content/assign). This page does NOT reach into a screen's own content
// beyond MIGRATING what MAIN/PLAYGROUND (or another layout's own regions)
// already held — building NEW per-cell content is the detailed editor's job,
// deliberately out of scope (see the brief: "do not expose nested layouts").
//
// ⚠️ Two backend bugs found and fixed while building this, both required for
// this to work at all, not just for it to be nice:
//   - POST /layout/create returned only {status,msg} — no id, so nothing
//     could point at what it just created. Now returns the created layout.
//   - GET /layout/{id} (showById) was a bare findOrFail($id) — any company
//     could read any other company's layout. Fixed to owned-or-global, same
//     shape as AspectRatioController::show(). This page depends on it to
//     read an existing tiled composition's own regions before switching away
//     from it — reversibility requires reading what's there now.
export const TILE_GEOMETRY = {
    halves: [
        { width: 50, height: 100, top_margin: 0, start_margin: 0 },
        { width: 50, height: 100, top_margin: 0, start_margin: 50 },
    ],
    mainside: [
        { width: 66.67, height: 100, top_margin: 0, start_margin: 0 },
        { width: 33.33, height: 100, top_margin: 0, start_margin: 66.67 },
    ],
    quarters: [
        { width: 50, height: 50, top_margin: 0, start_margin: 0 },
        { width: 50, height: 50, top_margin: 0, start_margin: 50 },
        { width: 50, height: 50, top_margin: 50, start_margin: 0 },
        { width: 50, height: 50, top_margin: 50, start_margin: 50 },
    ],
};

// Ported directly from the prototype's own COMPS[id].slots — a DIFFERENT
// slot list per composition, same as COMPS itself. The composition decides
// which slots exist; this page's content section renders whichever list the
// current composition returns, exactly like the prototype's own
// buildSections() reads from comp().slots rather than a fixed pair.
// Order matches TILE_GEOMETRY's own array order (region 0, 1, 2, 3 — the
// same order screens_setting/useCurrentCompositionRegions already use), NOT
// a left-to-right reading order — verified empirically (RTL grid
// auto-placement puts DOM-first at the visual right), so index 0 really is
// "القسم الأيمن" for halves, matching the prototype's own Z1.
// ⚠️ FIXED — halves' labels were assigned index0="right"/index1="left",
// matching how region 0 rendered BEFORE .sp-frame was forced to
// direction:ltr (a real, separate bug — RTL grid auto-placement put DOM-
// first at the visual right). Region 0's own GEOMETRY (TILE_GEOMETRY.halves
// [0].start_margin = 0, i.e. the PHYSICAL left edge, matching the same
// convention a real device/player uses — no text-direction concept at all)
// never changed; only the rendering was wrong. Now that rendering matches
// geometry (confirmed: region 0 sits at the same physical x in Arabic and
// English), the OLD labels became backwards — index 0 is physically left
// but was still being labeled "right." mainside/quarters are unaffected:
// their labels are role-based (main/side) or numbered (١/٢/٣/٤), never
// positional, so nothing to swap there.
export const REGION_ROLE_KEYS = {
    halves: ["region_halves_left", "region_halves_right"],
    mainside: ["region_mainside_main", "region_mainside_side"],
    quarters: ["region_quarters_1", "region_quarters_2", "region_quarters_3", "region_quarters_4"],
};
export const REGION_GUIDANCE_KEYS = {
    halves: ["guidance_region_any", "guidance_region_any"],
    mainside: ["guidance_region_mainside_main", "guidance_region_mainside_side"],
    quarters: ["guidance_region_any", "guidance_region_any", "guidance_region_any", "guidance_region_any"],
};

// Which named preset (if any) a layout's actual screens_setting matches —
// so the STAGE PREVIEW can use the prototype's own precomputed grid ratios
// (COMPOSITIONS[x].cols/rows: "1fr 1fr" for halves, "2fr 1fr" for
// mainside, 2×2 for quarters) instead of inventing new ones from raw
// percentages. Only layouts created by this page's own composition switch
// (TILE_GEOMETRY, above) will ever match — a layout built some other way
// (the classic/detailed editor) legitimately won't, and the caller falls
// back to an even N-column grid rather than pretending it's one of the three.
export function matchCompositionShape(screens) {
    const close = (a, b) => Math.abs((a ?? 0) - b) < 1;
    for (const [id, geometry] of Object.entries(TILE_GEOMETRY)) {
        if (screens.length !== geometry.length) continue;
        const matches = screens.every((s, i) => {
            const g = geometry[i];
            return close(s.width, g.width) && close(s.height, g.height) && close(s.top_margin, g.top_margin) && close(s.start_margin, g.start_margin);
        });
        if (matches) return id;
    }
    return null;
}

// ⚠️ CORRECTED — this used to call GET /layout/aspect-ratios. That route is
// dead: inside the /layout prefix group, `Route::get('/{id}', 'showById')`
// is registered BEFORE `Route::get('/aspect-ratios', 'getAspectRatios')`,
// so Laravel's first-match routing swallows "aspect-ratios" as the {id}
// wildcard and calls showById('aspect-ratios') — a 404 with an unrelated
// "Layout ID Not Found" body. LayoutController doesn't even have a
// getAspectRatios() method, so the route was never reachable regardless.
// Not a feature gate, not a scope — reproduced with a real authenticated
// request and the executed SQL before concluding this (see project memory:
// disabled-control-must-say-why). AspectRatioController's OWN endpoint
// (used by the classic Layout Apps page, confirmed via queries/layouts.js)
// is what actually works: ungated, no company scope (verified via captured
// SQL — `where company_id is null or company_id = ?`, nothing narrower).
// That's the one this calls now.
const getAspectRatios = async () => {
    const { data } = await axiosInstance.get("/aspect-ratio");
    return data.data;
};
export const useAspectRatiosForLayout = () =>
    useQuery(["showPageAspectRatios"], getAspectRatios, { staleTime: 1000 * 60 * 10, retry: false });

/**
 * What a region's raw rows can be turned back into elsewhere: one
 * POST /campaign/content/assign call per reference type present (playlist
 * ids and/or direct media ids — a region can hold either, or several media
 * rows at once, same as any MAIN/PLAYGROUND layer can today). A nested
 * layout (a region that is itself a tiled composition) is flagged
 * unmigratable rather than attempted — nested layouts are explicitly out of
 * scope, and silently flattening one would be worse than losing it loudly.
 *
 * displayName is what the confirm dialog names when this region's content
 * would be LOST (doesn't fit the target) — real names, not "some content".
 */
export function deriveMigratableRef(rows) {
    if (!rows || rows.length === 0) return null;

    if (rows.length === 1 && rows[0].layout_id) {
        return { unmigratable: true, assigns: [], displayName: rows[0].layout_title || rows[0].layout_id };
    }

    const playlistIds = [...new Set(rows.filter((r) => r.playlist_id).map((r) => r.playlist_id))];
    const mediaRows = rows.filter((r) => r.media_id);
    const assigns = [];
    const names = [];

    if (playlistIds.length) {
        assigns.push({ type: "playlist", ids: playlistIds });
        rows.filter((r) => r.playlist_id).forEach((r) => names.push(r.playlist_title));
    }
    if (mediaRows.length) {
        assigns.push({ type: "media", ids: mediaRows.map((r) => r.media_id) });
        mediaRows.forEach((r) => names.push(r.title || r.file_name));
    }
    if (!assigns.length) return null;

    const uniqueNames = [...new Set(names.filter(Boolean))];
    const displayName =
        uniqueNames.length <= 2 ? uniqueNames.join("، ") : `${uniqueNames.slice(0, 2).join("، ")} +${uniqueNames.length - 2}`;

    return { unmigratable: false, assigns, displayName: displayName || rows[0].file_name || rows[0].playlist_title };
}

const getRawLayerContent = async (campaignId, layer) => {
    const { data } = await axiosInstance.get(`/campaign/content/${campaignId}/${layer}`);
    return data.data;
};

const getRawPlaylistItems = async (playlistId, layer) => {
    const { data } = await axiosInstance.get(`/playlist/${playlistId}/${layer}/media`);
    return data.data;
};

/**
 * The CURRENT composition's regions, in order — used for TWO things off one
 * fetch (react-query caches by [key, layoutId], so CompositionPicker and
 * Stage share this instead of each fetching it separately):
 *   - `ref` (migratable assign spec + display name) for the composition
 *     switcher's own migrate/lost planning.
 *   - `fill`/`previewThumb`/`previewType` + the region's own geometry, for
 *     the STAGE PREVIEW to render "what's actually in this composition right
 *     now" — same shape useShowPageData already builds for MAIN/PLAYGROUND
 *     (deriveFill + borrow the first playlist item's thumb when the region
 *     is a playlist reference), just applied per-region instead of per-layer.
 *
 * "stack" isn't fetched — the caller already has MAIN's and PLAYGROUND's
 * rows from useShowPageData, no extra request needed. Only asked to fetch
 * when MAIN currently holds a layout: GET /layout/{id} for the real
 * screen ids (screens_setting, synced with real campaign ids by
 * ensureCampaignsForScreens), then one GET .../MAIN per screen, in order.
 */
export const useCurrentCompositionRegions = (layoutId) => {
    return useQuery(
        ["showPageCompositionRegions", layoutId],
        async () => {
            const { data: layoutResp } = await axiosInstance.get(`/layout/${layoutId}`);
            const screens = layoutResp.data.screens_setting || [];
            const regionRows = await Promise.all(screens.map((s) => getRawLayerContent(s.id, "MAIN")));
            const fills = regionRows.map((rows) => deriveFill(rows));
            const playlistItems = await Promise.all(
                fills.map((fill) => (fill?.kind === "playlist" ? getRawPlaylistItems(fill.id, "MAIN") : Promise.resolve(null)))
            );
            return screens.map((s, i) => {
                const fill = fills[i];
                const items = playlistItems[i];
                const previewThumb =
                    fill?.kind === "media" || fill?.kind === "multi" ? fill.thumb : fill?.kind === "playlist" ? items?.[0]?.thumb : null;
                const previewType =
                    fill?.kind === "media" || fill?.kind === "multi" ? fill.mediaType : fill?.kind === "playlist" ? items?.[0]?.type : null;
                return {
                    screenCampaignId: s.id,
                    width: s.width,
                    height: s.height,
                    top_margin: s.top_margin,
                    start_margin: s.start_margin,
                    fill,
                    previewThumb: previewThumb || null,
                    previewType: previewType || null,
                    ref: deriveMigratableRef(regionRows[i]),
                    // Same shape useShowPageData's own slots carry — RegionSlot
                    // builds a Picker-compatible `slot` object straight from this,
                    // no separate fetch needed to open a region's own picker.
                    rowIds: (regionRows[i] || []).map((r) => r.campaign_playlist_media_id ?? r.id).filter(Boolean),
                    rawRows: regionRows[i] || [],
                };
            });
        },
        { enabled: !!layoutId, staleTime: 0 }
    );
};

/**
 * Switches composition in ANY direction (stack↔tiled, tiled↔tiled) as one
 * migrate-then-repoint-then-clean-up sequence:
 *   1. Build the TARGET's region list — either the existing campaign's own
 *      MAIN/PLAYGROUND (target "stack") or a freshly created Layout's screens
 *      (POST /layout/create, same call LayoutSetup's own useCreateTemplate
 *      makes).
 *   2. Migrate each SOURCE region that fits into the target's region count,
 *      in order, via POST /campaign/content/assign (region 1→1, 2→2, …) —
 *      the caller (CompositionPicker) already computed this split and knows
 *      what's being left behind (the confirm dialog names it before this
 *      ever runs).
 *   3. Point MAIN at the new layout (tiled target only) — AFTER migrating,
 *      so a mid-sequence failure leaves the OLD MAIN reference intact rather
 *      than pointing at a half-filled new structure with nothing referencing
 *      the old content anymore.
 *   4. Delete the OLD MAIN row(s), and — switching stack→tiled only — the
 *      old PLAYGROUND row(s), now that their content lives in the new
 *      regions (or was disclosed as lost before this ran). Same
 *      add-then-delete-old ordering as every other swap on this page.
 *
 * The old layout+screens (switching AWAY from tiled) are left in place,
 * unreferenced — same as an abandoned playlist stays in the company's
 * library. Not cleaned up here; a known simplification, not a leak of
 * anything reachable.
 */
export const useSwitchComposition = () => {
    const queryClient = useQueryClient();
    return useMutation(
        async ({ campaignId, campaignName, targetCompositionId, aspectRatioId, migrating, oldMainRowIds, oldPlaygroundRowIds, oldAudioIndex }) => {
            let targets;
            let newLayoutId = null;

            if (targetCompositionId === "stack") {
                targets = [
                    { campaignId, layer: "MAIN" },
                    { campaignId, layer: "PLAYGROUND" },
                ];
            } else {
                // ⚠️ REUSE BEFORE CREATE — every switch used to call
                // /layout/create unconditionally, so quarters→stacked→
                // quarters made a SECOND "quarters" layout and abandoned the
                // first (region content and all — confirmed live: switching
                // back does NOT get you back your old region content, it
                // starts a blank new layout). Discriminator is (campaignId,
                // shape): GET /layout/for-campaign/{campaignId} lists every
                // layout this campaign's MAIN row has EVER pointed at
                // (active or since-abandoned — a raw DB query specifically
                // reaching past soft-deleted rows, see LayoutController::
                // forCampaign), matchCompositionShape() picks out ones
                // actually shaped like the target, most-recent wins on a
                // tie. This is the (campaign_id, shape) key the playlist
                // reuse decision explicitly lacked — that row had no
                // discriminator and was shared, this one is scoped to one
                // campaign's own history.
                let reusable = null;
                try {
                    const { data: candidates } = await axiosInstance.get(`/layout/for-campaign/${campaignId}`);
                    reusable = (candidates.data || []).find(
                        (l) => matchCompositionShape(l.screens_setting) === targetCompositionId
                    );
                } catch {
                    reusable = null;
                }

                if (reusable) {
                    newLayoutId = reusable.id;
                    targets = reusable.screens_setting.map((s) => ({ campaignId: s.id, layer: "MAIN" }));
                } else {
                    const { data: created } = await axiosInstance.post("/layout/create", {
                        title: `${campaignName} · ${targetCompositionId} · ${new Date().toISOString()}`,
                        aspect_ratio_id: aspectRatioId,
                        screens_setting: TILE_GEOMETRY[targetCompositionId],
                    });
                    newLayoutId = created.data.id;
                    targets = created.data.screens_setting.map((s) => ({ campaignId: s.id, layer: "MAIN" }));
                }
            }

            for (const item of migrating) {
                const target = targets[item.targetIndex];
                for (const a of item.ref.assigns) {
                    await axiosInstance.post("/campaign/content/assign", {
                        campaign_id: target.campaignId,
                        layer: target.layer,
                        type: a.type,
                        ids_list: a.ids,
                    });
                }
            }

            if (targetCompositionId !== "stack") {
                await axiosInstance.post("/campaign/content/assign", {
                    campaign_id: campaignId,
                    layer: "MAIN",
                    type: "layout",
                    ids_list: [newLayoutId],
                });
            }

            for (const rowId of oldMainRowIds || []) {
                await axiosInstance.delete(`/campaign/content/${rowId}/delete`);
            }
            if (targetCompositionId !== "stack") {
                for (const rowId of oldPlaygroundRowIds || []) {
                    await axiosInstance.delete(`/campaign/content/${rowId}/delete`);
                }
            }

            // ⚠️ The audio source must survive the switch the same way content
            // does — background→region 1, overlay→region 2 — or fall back to
            // صامت if the old source's index doesn't exist in the new target.
            // Left alone, this would do nothing (every freshly-assigned row
            // defaults to is_muted=0 at the DB column level — confirmed:
            // assignCampaignPlayLists()/assignCampaignMedia() never set it),
            // so migrating content into 2+ regions would silently leave ALL
            // of them audible at once — exactly the uncoordinated-multi-mute
            // gap this control exists to close, just reintroduced by the
            // switch itself if this step were skipped.
            //
            // The assign endpoints don't return the new row's id, so there's
            // no id to set is_muted on until the rows are re-read — one fresh
            // GET per target, then one is_muted write per row, audible only
            // for the carried-over (or none) index.
            const newAudibleIndex =
                oldAudioIndex !== null && oldAudioIndex !== undefined && oldAudioIndex >= 0 && oldAudioIndex < targets.length
                    ? oldAudioIndex
                    : -1;
            const targetContents = await Promise.all(targets.map((tg) => getRawLayerContent(tg.campaignId, tg.layer)));
            await Promise.all(
                targetContents.map((rows, i) => {
                    const muted = i !== newAudibleIndex;
                    return Promise.all(
                        (rows || []).map((r) =>
                            axiosInstance.post(`/campaign/media/${r.campaign_playlist_media_id ?? r.id}/setting`, {
                                is_muted: muted ? 1 : 0,
                            })
                        )
                    );
                })
            );
        },
        {
            onSuccess: (_, { campaignId }) => {
                invalidateShowPageLayer(queryClient, campaignId, "MAIN");
                invalidateShowPageLayer(queryClient, campaignId, "PLAYGROUND");
            },
        }
    );
};

/**
 * "لوحة المقهى" venue slots (CORNER logo, BANNER clock/weather) and any
 * layer that just needs ONE piece of direct media pointed at it, no
 * playlist wrapper. Same add-then-delete-old shape as useSwapLayerToPlaylist
 * (see that hook's own comment for why the ordering matters) — mirrored
 * rather than generalized into one hook, because the two request bodies
 * differ (`type`, `ids_list` vs `playlistId`) and a shared hook would need a
 * branch per call site anyway.
 *
 * Clock and Weather widgets are NOT a separate write path — confirmed
 * against ClockList.jsx / WeatherList.jsx (the classic builder's own
 * pickers): both are Media rows (media.type = CLOCK / WEATHER, sidecar
 * `clocks`/`weathers` tables keyed by media_id) attached through this exact
 * `type: "media"` call. One hook covers the logo AND the clock/weather case.
 */
export const useSwapLayerToMedia = () => {
    const queryClient = useQueryClient();
    return useMutation(
        async ({ campaignId, layer, mediaId, oldRowIds }) => {
            // Same null-guard as useSwapLayerToPlaylist — "إزالة هذا العنصر" calls
            // this with mediaId: null to just clear the venue slot.
            if (mediaId) {
                await axiosInstance.post("/campaign/content/assign", {
                    campaign_id: campaignId,
                    layer,
                    type: "media",
                    ids_list: [mediaId],
                });
            }
            for (const rowId of oldRowIds || []) {
                await axiosInstance.delete(`/campaign/content/${rowId}/delete`);
            }
        },
        {
            onSuccess: (_, { campaignId, layer }) => invalidateShowPageLayer(queryClient, campaignId, layer),
        }
    );
};

// GET /clock/all and /weather/all — the classic builder's own catalog reads
// for BANNER (ClockList.jsx / WeatherList.jsx). Both are gated server-side by
// check.feature:clock / check.feature:weather (CheckFeature middleware, 402
// when the company's package doesn't include it) — surfaced to the picker
// via isError/error, not swallowed, since a 402 here means "this company's
// plan doesn't include this," not "empty list."
const getCompanyClocks = async () => {
    const { data } = await axiosInstance.get("/clock/all");
    return data.data;
};
export const useCompanyClocks = (enabled) =>
    useQuery(["showPageClocks"], getCompanyClocks, { enabled, staleTime: 1000 * 60, retry: false });

const getCompanyWeathers = async () => {
    const { data } = await axiosInstance.get("/weather/all");
    return data.data;
};
export const useCompanyWeathers = (enabled) =>
    useQuery(["showPageWeathers"], getCompanyWeathers, { enabled, staleTime: 1000 * 60, retry: false });

// GET /rss/feeds (kind:"rss", source-URL feeds) and GET /rss/feeds/custom
// (kind:"rss_custom" — any non-empty route segment flips RssFeedController::
// getRss()'s `request()->type` check to RSS_CUSTOM). ⚠️ NOT /media: both
// getLayerMedia (section=MEDIA) and its own LINKS branch explicitly
// `whereNotIn('type', [...,'RSS','RSS_CUSTOM',...])` — RSS media are
// deliberately invisible to the generic library, this dedicated endpoint is
// the only way to list them. Same 402-means-plan-gate handling as
// clock/weather (check.feature:rss_feed).
const getCompanyRssFeeds = async ({ queryKey }) => {
    const [, kind] = queryKey;
    const { data } = await axiosInstance.get(kind === "rss_custom" ? "/rss/feeds/custom" : "/rss/feeds", {
        params: { per_page: 50 },
    });
    return data.data;
};
export const useCompanyRssFeeds = (kind, enabled) =>
    useQuery(["showPageRssFeeds", kind], getCompanyRssFeeds, { enabled, staleTime: 1000 * 60, retry: false });

/**
 * Adds a new source-URL RSS feed (POST /rss/add) and attaches it to the
 * layer in one action. /rss/add returns only {status,msg} — no id, same
 * shape gap /layout/create has — so the new row is found by re-listing
 * /rss/feeds and matching the exact file_name RssFeedController::
 * nameHandler() produces (name + ".xml"), then attached through the same
 * swap-to-media path every other venue pick uses.
 *
 * ⚠️ rss_settings sends EVERY key rssSettingHandler() reads, not just
 * position. Caught by a real backend test, not by reasoning about the code:
 * most keys there read `$rssSettings['x'] ?? default` (safe to omit — `??`
 * on a bare array access suppresses the undefined-index warning), but the
 * boolean-flavored ones read `$rssSettings['x'] === "true" ?? false` — the
 * `===` runs FIRST, against a key that doesn't exist, so PHP raises the
 * warning before `??` ever gets a turn; same trap for the two cast fields
 * (`(int) $rssSettings['x'] ?? default` — the cast breaks `??`'s bare-
 * array-access special case). Omitting any of those threw "Undefined array
 * key" and 500'd the request. position has no fallback at all regardless.
 */
export const useCreateAndAttachRssFeed = () => {
    const queryClient = useQueryClient();
    return useMutation(
        async ({ campaignId, layer, url, label, oldRowIds }) => {
            const name = `${(label || "feed").trim().slice(0, 20).replace(/\s+/g, "_") || "feed"}-${Date.now()}`;
            await axiosInstance.post("/rss/add", {
                name,
                url,
                rss_settings: {
                    position: "BOTTOM",
                    play_video_full_length: "false",
                    display_video_content: "false",
                    image_fullscreen: "false",
                    original_layout: "false",
                    image_blending: "false",
                    stretch_image: "false",
                    show_image: "true",
                    mute_video: "true",
                    posts_per_page: 1,
                    speed: 1,
                    duration: 20,
                },
            });
            const expectedFileName = `${name}.xml`;
            const { data } = await axiosInstance.get("/rss/feeds", { params: { per_page: 50 } });
            const created = (data.data || []).find((m) => m.file_name === expectedFileName);
            if (!created) {
                throw new Error("RSS feed created but could not be located to attach.");
            }
            await axiosInstance.post("/campaign/content/assign", {
                campaign_id: campaignId,
                layer,
                type: "media",
                ids_list: [created.id],
            });
            for (const rowId of oldRowIds || []) {
                await axiosInstance.delete(`/campaign/content/${rowId}/delete`);
            }
        },
        {
            onSuccess: (_, { campaignId, layer }) => {
                invalidateShowPageLayer(queryClient, campaignId, layer);
                queryClient.invalidateQueries(["showPageRssFeeds"]);
            },
        }
    );
};

/**
 * الصوت من — which SOURCE is audible: one of MAIN/PLAYGROUND in the stacked
 * composition, or one of N regions in a tiled one. There is no dedicated
 * audio-source field: `campaign_playlist_media.is_muted` (a real column,
 * already used by the classic builder's per-item mute toggle) is set on
 * EVERY row of the chosen group (unmuted) and every row of every OTHER group
 * (muted) — `chosenKey: null` mutes all of them ("صامت"). Fanned out with
 * POST /campaign/media/{id}/setting per row (campaignMediaSetting on the
 * backend) — the same endpoint PlaylistSettingPopup.jsx already calls for
 * this exact field, just reused here across whole groups instead of one row.
 *
 * `groups` is `[{key, rowIds}]` — the STACK caller passes exactly two
 * (MAIN, PLAYGROUND), a TILED caller passes one per region (screenCampaignId
 * as key). Generalized rather than kept as a fixed MAIN/PLAYGROUND pair
 * once a tiled composition could ALSO have two-or-more simultaneously
 * unmuted rows with nothing coordinating them — the same bug shape, just
 * with more than two groups now able to collide.
 *
 * Deliberately does NOT touch campaigns.settings.live.layer_settings (the
 * "layer policy" bulk override read by DeviceCampaignContent::
 * applyLayerPolicyToMedia() when apply_layer_settings=true) — that's a
 * separate, coarser mechanism this control must leave alone. As long as
 * apply_layer_settings stays off (its default), the per-row values written
 * here are what's actually served.
 */
export const useSetLayerAudioSource = () => {
    const queryClient = useQueryClient();
    return useMutation(
        async ({ campaignId, groups, chosenKey, layoutId }) => {
            const setRows = (rowIds, muted) =>
                Promise.all(
                    (rowIds || []).map((id) => axiosInstance.post(`/campaign/media/${id}/setting`, { is_muted: muted ? 1 : 0 }))
                );
            await Promise.all(groups.map((g) => setRows(g.rowIds, g.key !== chosenKey)));
        },
        {
            onSuccess: (_, { campaignId, layoutId }) => {
                invalidateShowPageLayer(queryClient, campaignId, "MAIN");
                invalidateShowPageLayer(queryClient, campaignId, "PLAYGROUND");
                invalidateCompositionRegions(queryClient, layoutId);
            },
        }
    );
};

/**
 * موضع الطبقة العليا — PLAYGROUND's on-screen box, in the stack composition
 * only. Same settings JSON every geometry control in this app already writes
 * (width/height/top_margin/start_margin, percent — see PlaylistSettingPopup.
 * jsx's CornerAreaControl for the corner-box analog, and campaignMediaSetting
 * on the backend, which MERGES this into the row's existing settings rather
 * than replacing it). Fanned out across every PLAYGROUND row, same as audio.
 *
 * "full" is this app's own existing default (CampaignContentObserver::
 * defaultSettings()), not invented here. "side" and "window" are considered
 * but NOT spec'd anywhere in this codebase — there is no existing PLAYGROUND
 * position picker to copy values from, so these two are original geometry
 * choices, not ported ones (unlike almost everything else on this page).
 */
export const GEO_PRESETS = {
    full: { width: 100, height: 100, top_margin: 0, start_margin: 0 },
    side: { width: 32, height: 100, top_margin: 0, start_margin: 68 },
    box: { width: 38, height: 40, top_margin: 8, start_margin: 54 },
};

export const useSetPlaygroundGeometry = () => {
    const queryClient = useQueryClient();
    return useMutation(
        async ({ campaignId, geo, rowIds }) => {
            const settings = GEO_PRESETS[geo] || GEO_PRESETS.full;
            await Promise.all(
                (rowIds || []).map((id) => axiosInstance.post(`/campaign/media/${id}/setting`, { settings }))
            );
        },
        {
            onSuccess: (_, { campaignId }) => invalidateShowPageLayer(queryClient, campaignId, "PLAYGROUND"),
        }
    );
};

/**
 * POST /campaign/{id}/publish — the existing, untouched endpoint (same one
 * CampaignSetup.jsx's own Publish button calls). Invalidates this page's own
 * query keys; usePublishCampaignMutation in campaigns.js only invalidates
 * when the URL contains "campaign", which /shows/:id never does.
 */
export const usePublishShowCampaign = () => {
    const queryClient = useQueryClient();
    return useMutation(
        async (campaignId) => {
            const { data } = await axiosInstance.post(`/campaign/${campaignId}/publish`, {});
            return data.data;
        },
        {
            onSuccess: (_, campaignId) => {
                queryClient.invalidateQueries(["getCampaign", campaignId]);
            },
        }
    );
};
